import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import "semantic-ui-css/semantic.min.css";
import "./App.css";
import "../node_modules/swagger-ui/dist/swagger-ui.css";
import SwaggerUI from "swagger-ui";
import "./swagger-feeling-blue.css";
import { Icon, Menu, Segment, Sidebar, Grid, Container, Button } from "semantic-ui-react";
import HDHeader from "./HDHeader/HDHeader";
import HDFooter from "./HDFooter/HDFooter";
import attribText from "./attributions.js";

export type apiTypes =
    | "overview"
    | "attribution"
    | "sentience"
    | "realtime"
    | "historic"
    | "predictive";

export const formatApiTypes = (apiType: apiTypes) => {
    return {
        overview: "Overview",
        attribution: "Attribution",
        sentience: "BAA",
        realtime: "Realtime",
        historic: "Historic",
        predictive: "Predictive"
    }[apiType];
};

const App = () => {
    const [activeApi, setActiveApi] = useState<apiTypes>("overview");
    const [showSidebar, setShowSidebar] = useState(true);

    /* Note: We set the filter element to filter by "default" but then via
       CSS hide the element, essentially filtering what gets displayed */
    useEffect(() => {
        if (activeApi === "sentience") {
            SwaggerUI({
                domNode: document.getElementById("api-data"),
                url: "https://sentience-api.svc.hawadawa.com/api-docs",
                supportedSubmitMethods: [],
//   	      filter: "default"
            });
        } else if (activeApi === "realtime") {
            SwaggerUI({
                domNode: document.getElementById("api-data"),
                url: "https://realtime-api.svc.hawadawa.com/docs/openapi/realtime.json",
                supportedSubmitMethods: [],
//                filter: "default"
            });
        } else if (activeApi === "historic") {
            SwaggerUI({
                domNode: document.getElementById("api-data"),
                url: "https://realtime-api.svc.hawadawa.com/docs/openapi/history.json",
                supportedSubmitMethods: [],
//                filter: "default"
            });
        } else if (activeApi === "predictive") {
            SwaggerUI({
                domNode: document.getElementById("api-data"),
                url: "https://realtime-api.svc.hawadawa.com/docs/openapi/predictive.json",
                supportedSubmitMethods: [],
//                filter: "default"
            });
        }
    }, [activeApi]);

    const menuItems = (
        <>
            <Menu.Item disabled>
                <h3>General</h3>
            </Menu.Item>
            <Menu.Item
                name="overview"
                active={activeApi === "overview"}
                onClick={() => {
                    setActiveApi("overview");
                    setShowSidebar(!showSidebar);
                }}>
                <Icon name="dashboard" />
                Overview
            </Menu.Item>
            <Menu.Item
                name="attribution"
                active={activeApi === "attribution"}
                onClick={() => {
                    setActiveApi("attribution");
                    setShowSidebar(!showSidebar);
                }}>
                <Icon name="copyright" />
                Data Attributions
            </Menu.Item>
            <Menu.Item disabled>
                <h3>Station-based</h3>
            </Menu.Item>
            <Menu.Item
                name="sentience"
                active={activeApi === "sentience"}
                onClick={() => {
                    setActiveApi("sentience");
                    setShowSidebar(!showSidebar);
                }}>
                <Icon name="file outline" />
                {formatApiTypes("sentience")} API
            </Menu.Item>
            <Menu.Item disabled>
                <h3>Location-based</h3>
            </Menu.Item>
            <Menu.Item
                name="realtime"
                active={activeApi === "realtime"}
                onClick={() => {
                    setActiveApi("realtime");
                    setShowSidebar(!showSidebar);
                }}>
                <Icon name="time" />
                {formatApiTypes("realtime")} API
            </Menu.Item>
            <Menu.Item
                name="historic"
                active={activeApi === "historic"}
                onClick={() => {
                    setActiveApi("historic");
                    setShowSidebar(!showSidebar);
                }}>
                <Icon name="history" />
                {formatApiTypes("historic")} API
            </Menu.Item>
            <Menu.Item
                name="predictive"
                active={activeApi === "predictive"}
                onClick={() => {
                    setActiveApi("predictive");
                    setShowSidebar(!showSidebar);
                }}>
                <Icon name="history" flipped="horizontally" />
                {formatApiTypes("predictive")} API
            </Menu.Item>
        </>
    );

    const apiLink = (api: apiTypes) => (
        <h3>
            <Button
                onClick={() => {
                    setActiveApi(api);
                    setShowSidebar(false);
                }}
                style={{
                    background: "none",
                    border: "none",
                    padding: 0
                }}>
                <h3>{formatApiTypes(api)} API</h3>
            </Button>
        </h3>
    );

    return (
        <div className="App">
            <HDHeader
                activeApi={activeApi}
                toggleSidebar={() => setShowSidebar(!showSidebar)}
                changeActiveApi={(api: apiTypes) => {
                    setActiveApi(api);
                    if (api === "overview") setShowSidebar(true);
                }}
            />
            <Sidebar.Pushable
                as={Segment}
                style={{ marginTop: "60px", marginBottom: 0, border: "none" }}>
                <Sidebar
                    as={Menu}
                    animation="overlay"
                    vertical
                    visible={showSidebar}
                    style={{
                        borderTop: "none",
                        borderBottom: "none",
                        borderRight: "1px solid #e1e1e1",
                        width: "183px",
                        boxShadow: "none",
                        backgroundColor: "#f8f8f8"
                    }}>
                    {menuItems}
                </Sidebar>

                {activeApi === "overview" && (
                    <Sidebar.Pusher style={{ marginLeft: showSidebar ? "200px" : 0 }}>
                        <Container
                            className="overview"
                            style={{
                                minHeight: "700px",
                                paddingTop: "50px",
                                marginBottom: "30px"
                            }}>
                            <h1>API DOCUMENTATION PORTAL</h1>
                            <p className="header">
                                This page contains technical details for our four API products and
                                their endpoints.
                            </p>

                            <Grid stackable columns={2}>
                                <Grid.Column width={4}>
                                    <Grid.Row>
                                        <h2>Station-based</h2>
                                    </Grid.Row>
                                </Grid.Column>
                                <Grid.Column width={12}>
                                    <Grid.Row>
                                        {apiLink("sentience")}
                                        <p>
                                            The BAA API offers downloads of metadata for a
                                            customer's <em>BAA</em> measurement network as
                                            well as historical data from the network in machine
                                            readable formats like CSV and XLSX.
                                        </p>
                                    </Grid.Row>
                                </Grid.Column>
                            </Grid>

                            <hr />

                            <Grid stackable columns={2}>
                                <Grid.Column width={4}>
                                    <Grid.Row>
                                        <h2>Location-based</h2>
                                    </Grid.Row>
                                </Grid.Column>
                                <Grid.Column width={12}>
                                    <Grid.Row>
                                        {apiLink("realtime")}
                                        <p>
                                            The Realtime API offers current values for air
                                            pollutants and pollen. There is one endpoint for each
                                            type of pollution/pollen and in addition the European
                                            air quality index (AQI) as well as different health risk
                                            indices (e.g. for Asthma or COPD) are offered.
                                        </p>
                                    </Grid.Row>
                                    <Grid.Row>
                                        {apiLink("historic")}
                                        <p>
                                            The Historic Air Quality API offers recorded historic
                                            air pollution/pollen values. There is one endpoint for
                                            each type of pollution/pollen and in addition the
                                            European air quality index (AQI) as well as different
                                            health risk indices (e.g. for Asthma or COPD) are
                                            offered.
                                        </p>
                                    </Grid.Row>
                                    <Grid.Row>
                                        {apiLink("predictive")}
                                        <p>
                                            The Predictive Air Quality API offers current as well as
                                            predicted values for air pollutants and pollen. There is
                                            one endpoint for each type of pollution/pollen and in
                                            addition air quality and health indices are offered.
                                        </p>
                                    </Grid.Row>
                                </Grid.Column>
                            </Grid>
                        </Container>
                    </Sidebar.Pusher>
                )}
                {activeApi === "attribution" && (
                    <Sidebar.Pusher>
                        <Container
                            className="overview"
                            style={{
                                minHeight: "700px",
                                paddingTop: "50px",
                                marginBottom: "50px"
                            }}>
                            <h1>DATA ATTRIBUTIONS</h1>
                            <p>
                                In addition to our own data, Bernard Technologies GmbH makes use of the following
                                data sources to provide station-specific and model-based data.
                            </p>
                            <div>
                                <ReactMarkdown children={attribText} />
                            </div>
                        </Container>
                    </Sidebar.Pusher>
                )}
                {["sentience", "realtime", "historic", "predictive"].includes(activeApi) && (
                    <Sidebar.Pusher>
                        <div id="api-data" style={{ minHeight: "500px" }} />
                    </Sidebar.Pusher>
                )}
            </Sidebar.Pushable>
            <HDFooter />
        </div>
    );
};

export default App;
